import { Link } from 'react-router-dom';

const VALUES = [
  { icon: 'ri-hand-heart-line', title: 'Honest by Design', desc: 'We assess your situation and recommend only what you genuinely need. No overselling, no scare tactics — ever.' },
  { icon: 'ri-group-line', title: 'Community Focused', desc: 'Based in Oakville since 2002, we\'ve built our reputation one GTA family at a time, through consistency and care.' },
  { icon: 'ri-medal-line', title: 'Certified Excellence', desc: 'T.S.S.A.-approved and continuously trained on the latest HVAC systems, technology, and safety standards.' },
  { icon: 'ri-sparkling-line', title: 'Respect for Your Home', desc: 'We protect floors, walls, and work areas throughout every job — and leave your space spotless when we\'re done.' },
];

const MILESTONES = [
  { year: '2002', title: 'Founded in Oakville', desc: 'Atlantis HVAC Systems Inc. was established with a single mission: deliver honest, expert HVAC service to GTA homeowners.' },
  { year: '2006', title: 'T.S.S.A. Certification', desc: 'Achieved full Technical Standards and Safety Authority approval, cementing our commitment to the highest Ontario safety standards.' },
  { year: '2010', title: 'GTA East Expansion', desc: 'Launched our dedicated Toronto and Etobicoke service team, making Atlantis\'s service accessible across the full GTA corridor.' },
  { year: '2018', title: 'Air Quality Specialization', desc: 'Expanded our indoor air quality division, adding Lennox PureAir™, HRV systems, and UV air purification to our service offering.' },
  { year: '2022', title: '20 Years of Service', desc: 'Celebrated two decades of trusted HVAC service across the Greater Toronto Area, recognized with consistent 4.9-star ratings on HomeStars.' },
];

export default function AboutStory() {
  return (
    <div>
      {/* Story Section */}
      <section className="bg-luxury-cream py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="font-caps text-[11px] tracking-[0.25em] uppercase text-luxury-text-muted block mb-4">Our Story</span>
              <h2 className="font-serif text-3xl lg:text-5xl text-luxury-text-dark leading-tight mb-6">
                Built on Trust,<br />
                <em className="not-italic text-luxury-gold-dark">Grown Through Service</em>
              </h2>
              <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-5">
                Atlantis HVAC Systems Inc. was founded in 2002 with a clear purpose: to offer Greater Toronto Area homeowners a genuinely honest alternative in a market full of pressure tactics and overselling.
              </p>
              <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-5">
                Over two decades later, that commitment remains unchanged. Our T.S.S.A.-approved technicians bring the same level of care, expertise, and professionalism to every call — whether it&apos;s a 2 a.m. emergency or a scheduled spring tune-up.
              </p>
              <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-8">
                Operating from our head office at 6-1200 Speers Road, Oakville, we serve the full GTA with two dedicated regional teams: GTA West covering Oakville, Burlington, and Mississauga, and GTA East serving Toronto, Etobicoke, and surrounding areas.
              </p>
              <div className="flex gap-4 flex-wrap">
                <a
                  href="tel:9058454009"
                  className="inline-flex items-center gap-2 bg-luxury-gold hover:bg-luxury-gold-light text-luxury-black font-caps font-semibold text-xs tracking-[0.1em] uppercase px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-xs" />
                  </div>
                  <span>Talk to Our Team</span>
                </a>
                <Link
                  to="/locations"
                  className="inline-flex items-center gap-2 border border-luxury-gold/30 hover:border-luxury-gold text-luxury-gold-dark font-caps text-xs tracking-[0.1em] uppercase px-6 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span>Our Service Areas</span>
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {VALUES.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-luxury-gold/10 hover:border-luxury-gold/25 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-luxury-gold/10 mb-4">
                    <i className={`${v.icon} text-luxury-gold-dark text-lg`} />
                  </div>
                  <h3 className="font-serif text-luxury-text-dark text-base font-semibold mb-2">{v.title}</h3>
                  <p className="font-sans text-xs text-luxury-text-muted leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-luxury-dark py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="font-caps text-[11px] tracking-[0.25em] uppercase text-luxury-gold/60 block mb-4">Our Journey</span>
            <h2 className="font-serif text-3xl lg:text-5xl text-luxury-off-white leading-tight">
              Two Decades of <em className="not-italic text-luxury-gold">Milestones</em>
            </h2>
          </div>
          <div className="max-w-2xl mx-auto relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-luxury-gold/30 via-luxury-gold/15 to-transparent" />
            <div className="flex flex-col gap-10">
              {MILESTONES.map((m) => (
                <div key={m.year} className="relative flex gap-6 items-start pl-14">
                  <div className="absolute left-0 top-1 w-12 h-12 flex items-center justify-center rounded-full border border-luxury-gold/30 bg-luxury-black flex-shrink-0">
                    <span className="font-serif text-luxury-gold text-xs font-bold">{m.year}</span>
                  </div>
                  <div>
                    <h3 className="font-serif text-luxury-off-white text-lg font-semibold mb-2">{m.title}</h3>
                    <p className="font-sans text-sm text-luxury-off-white/50 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
